// src/pages/Location.js
import React from "react";
import { MapPin, Clock } from "lucide-react";

import Config from "../Config.json";
import ICON from "../assets/icons/favicon.ico";

const TITLE = `${Config.SITE_TITLE} - Localização`;
const DESC = "Conheça o espaço físico da Deli da Persy no Cariri.";
const CANONICAL = `${Config.SITE_DOMAIN}/local`;

function Location() {
    return (
        <main className="min-h-screen bg-[#FFF5EE] py-12 md:py-20 px-4 md:px-8 relative font-sans text-stone-800">
            <title>{TITLE}</title>
            <link rel="icon" href={ICON} />
            <link rel="canonical" href={CANONICAL} />
            <meta name="description" content={DESC} />

            {/* Textura de Fundo Vintage (Opcional para manter consistência) */}
            <div className="absolute top-0 left-0 w-full h-full opacity-[0.03] pointer-events-none bg-[url('https://www.transparenttextures.com/patterns/aged-paper.png')]" />

            <div className="max-w-6xl mx-auto relative z-10 animate-in fade-in slide-in-from-bottom-4 duration-500">
                
                {/* Título da Página */}
                <div className="mb-10 text-center border-b border-[#dbcdac]/40 pb-6">
                    <h1 className="text-3xl md:text-4xl font-serif font-bold text-[#528a89]">
                        Nossa Localização
                    </h1>
                    <p className="mt-2 text-stone-500 font-light">
                        Venha conhecer nosso acervo e aproveitar nossa gastronomia.
                    </p>
                </div>

                {/* Grid de 2 Colunas */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    
                    {/* COLUNA ESQUERDA: Informações */}
                    <div className="space-y-6 md:space-y-8">
                        
                        {/* Card 1: Endereço */}
                        <div className="bg-white rounded-2xl shadow-sm border border-[#dbcdac]/40 p-6 md:p-8 hover:shadow-[0_8px_20px_-10px_rgba(82,138,137,0.15)] transition-shadow duration-300">
                            <h2 className="font-serif text-xl md:text-2xl text-[#971253] font-bold flex items-center gap-3 mb-6">
                                <div className="p-2 bg-[#528a89]/10 rounded-full">
                                    <MapPin size={24} className="text-[#528a89]" />
                                </div>
                                Onde estamos
                            </h2>
                            <div className="space-y-1 ml-2">
                                <p className="text-xl md:text-2xl font-bold text-gray-800 tracking-tight">
                                    Rua Padre Cícero, 905 A
                                </p>
                                <p className="text-gray-500 font-medium text-sm md:text-base">
                                    Juazeiro do Norte - CE, 63010-021
                                </p>
                            </div>
                        </div>

                        {/* Card 2: Horários */}
                        <div className="bg-white rounded-2xl shadow-sm border border-[#dbcdac]/40 p-6 md:p-8 hover:shadow-[0_8px_20px_-10px_rgba(82,138,137,0.15)] transition-shadow duration-300">
                            <h2 className="font-serif text-xl md:text-2xl text-[#971253] font-bold flex items-center gap-3 mb-6">
                                <div className="p-2 bg-[#528a89]/10 rounded-full">
                                    <Clock size={24} className="text-[#528a89]" />
                                </div>
                                Horário de Funcionamento
                            </h2>
                            <div className="flex flex-col ml-2">
                                <div className="flex justify-between items-center py-3 border-b border-gray-100">
                                    <span className="font-bold text-gray-700">Sextas</span>
                                    <span className="text-gray-500 font-medium bg-gray-50 px-3 py-1 rounded-md">18:00 – 00:00</span>
                                </div>
                                <div className="flex justify-between items-center py-3 border-b border-gray-100">
                                    <span className="font-bold text-gray-700">Sábados</span>
                                    <span className="text-gray-500 font-medium bg-gray-50 px-3 py-1 rounded-md">16:00 – 00:00</span>
                                </div>
                                <div className="flex justify-between items-center py-3">
                                    <span className="font-bold text-gray-700">Domingos</span>
                                    <span className="text-gray-500 font-medium bg-gray-50 px-3 py-1 rounded-md">16:00 – 22:00</span>
                                </div>
                            </div>
                        </div>

                    </div>

                    {/* COLUNA DIREITA: Mapa e Uber */}
                    <div className="bg-white rounded-2xl shadow-sm border border-[#dbcdac]/40 p-4 md:p-6 flex flex-col hover:shadow-[0_8px_20px_-10px_rgba(82,138,137,0.15)] transition-shadow duration-300">
                        
                        {/* Iframe Wrapper */}
                        <div className="w-full h-[350px] md:h-[420px] rounded-xl overflow-hidden shadow-inner border border-gray-200">
                            <iframe 
                                title="Mapa Deli da Persy" 
                                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3947.2144781481973!2d-39.32287692591603!3d-7.206181770731427!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x7a178b172a8c3d7%3A0x6b772c72b202c611!2sR.%20Padre%20C%C3%ADcero%2C%20905%20A%20-%20Centro%2C%20Juazeiro%20do%20Norte%20-%20CE%2C%2063010-021!5e0!3m2!1spt-BR!2sbr!4v1729187910368!5m2!1spt-BR!2sbr"
                                width="100%" 
                                height="100%"
                                allowFullScreen="" 
                                loading="lazy" 
                                referrerPolicy="no-referrer-when-downgrade"
                                className="border-0 w-full h-full"
                            ></iframe>
                        </div>
                        
                        {/* Uber Button */}
                        <div className="mt-8 mb-2 flex justify-center">
                            <a 
                                href="https://m.uber.com/ul/?client_id=kqNYYvTTgVp1D_aqG75TFr-cNwOnPSrH&action=setPickup&pickup=my_location&dropoff[latitude]=-7.206186711818091&dropoff[longitude]=-39.32030195337132"
                                className="hover:scale-105 hover:-translate-y-1 transition-all duration-300 ease-out"
                                aria-label="Pedir um Uber para a Deli da Persy"
                            >
                                <picture>
                                    <source srcSet="https://dguests.com/assets/images/button/button-uber.webp" type="image/webp" />
                                    <source srcSet="https://dguests.com/assets/images/button/button-uber.png" type="image/png" />
                                    <img 
                                        src="https://dguests.com/assets/images/button/button-uber.png" 
                                        alt="Pedir Uber"
                                        className="w-[260px] md:w-[288px] h-auto drop-shadow-md rounded-xl"
                                    />
                                </picture>
                            </a>
                        </div>

                    </div>

                </div>
            </div>
        </main>
    );
}

export default Location;